// src/modules/accounting/pages/ExpensesPage.jsx
import React, { useEffect, useState, useRef } from 'react';
import {
  Button, Typography, Tag, Row, Col, Modal, Form, Input, InputNumber,
  Select, DatePicker, message, Space, Card, Statistic, Popconfirm, Divider,
  Radio, Alert, Dropdown
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, FilterOutlined,
  CreditCardOutlined, PrinterOutlined, DownOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  fetchExpenses, createExpense, updateExpense, deleteExpense,
  getExpenseSummary, fetchUsers, fetchFunds, payExpense, getAccounts,
  fetchExpenseVoucherPdf, fetchExpenseVouchersPdf,
} from '../api';
import { EXPENSE_CATEGORY_MAP, DEFAULT_EXPENSE_ACCOUNT_CODE } from '../constants';
import PaymentSplitInput from '../../../components/PaymentSplitInput';
import { fetchBranches } from '../../branches/api';
import { fetchCashLedgerBalance } from '../../treasury/api';
import DenominationCountForm from '../../treasury/components/DenominationCountForm';
import DataTable from '../../../components/UI/DataTable';

const { Title, Text } = Typography;
const fmt = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

const CATEGORIES = [
  { value: 'SALARY', label: 'Salary' },
  { value: 'RENT', label: 'Rent' },
  { value: 'ELECTRICITY', label: 'Electricity' },
  { value: 'INTERNET_TELECOM', label: 'Internet & Telecom' },
  { value: 'OFFICE_SUPPLIES', label: 'Office Supplies' },
  { value: 'MARKETING_ONLINE', label: 'Marketing (Online)' },
  { value: 'MARKETING_OFFLINE', label: 'Marketing (Offline)' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'LEGAL_COMPLIANCE', label: 'Legal & Compliance' },
  { value: 'GOLD_TESTING', label: 'Gold Testing / Assay' },
  { value: 'PACKAGING_LOGISTICS', label: 'Packaging & Logistics' },
  { value: 'SECURITY', label: 'Security' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'SOFTWARE_SUBSCRIPTIONS', label: 'Software / SaaS' },
  { value: 'BANK_CHARGES', label: 'Bank Charges' },
  { value: 'TAXES_DUTIES', label: 'Taxes & Duties' },
  { value: 'DEPRECIATION', label: 'Depreciation' },
  { value: 'MISCELLANEOUS', label: 'Miscellaneous' },
];

const CATEGORY_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

const CATEGORY_COLORS = {
  SALARY: 'blue', RENT: 'purple', ELECTRICITY: 'gold', MARKETING_ONLINE: 'green',
  MARKETING_OFFLINE: 'cyan', GOLD_TESTING: 'magenta', TAXES_DUTIES: 'red',
  INSURANCE: 'geekblue', MISCELLANEOUS: 'default',
};

const STATUS_COLORS = { PENDING: 'orange', APPROVED: 'blue', PAID: 'green', REJECTED: 'red', REVERSED: 'default' };
const PAYMENT_MODES = ['CASH', 'BANK_TRANSFER', 'UPI', 'CHEQUE', 'CREDIT_CARD', 'AUTO_DEBIT'];
const RECURRENCES = [
  { value: 'ONE_TIME', label: 'One-Time' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'YEARLY', label: 'Yearly' },
];

export default function ExpensesPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [funds, setFunds] = useState([]);
  const [branches, setBranches] = useState([]);
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentForm] = Form.useForm();
  const [payMode, setPayMode] = useState('FUND'); // 'DRAWER' | 'FUND'
  const [drawerBalance, setDrawerBalance] = useState(null);
  const [denomCounts, setDenomCounts] = useState({});
  const [messageApi, contextHolder] = message.useMessage();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [printing, setPrinting] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const category = Form.useWatch('category', form);

  const [allocationRatios, setAllocationRatios] = useState({ basic: 60, hra: 20, allowances: 20 });
  const [profTaxRate, setProfTaxRate] = useState(2);

  // Watch salary components to compute suggestions
  const salaryComponents = Form.useWatch('salaryComponents', form) || {};
  const [salarySuggestions, setSalarySuggestions] = useState({ basic: 0, hra: 0, allowances: 0, bonus: 0 });
  const lastAutoFillRef = useRef(null);
  const lastInputRef = useRef(null);

  useEffect(() => {
    if (!modalOpen) return;

    const netPay = Number(salaryComponents.netPay || 0);
    const deductions = Number(salaryComponents.deductions || 0);
    const bonus = Number(salaryComponents.bonus || 0);

    const inputKey = JSON.stringify({ netPay, deductions, bonus, allocationRatios, profTaxRate });
    if (lastInputRef.current === inputKey) {
      return;
    }
    lastInputRef.current = inputKey;

    if (!netPay && !deductions) {
      setSalarySuggestions({ basic: 0, hra: 0, allowances: 0, bonus: bonus || 0, professionalTax: 0 });
      return;
    }
    const gross = netPay + deductions;
    const baseForSplit = Math.max(0, gross - bonus);
    const ratioSum = allocationRatios.basic + allocationRatios.hra + allocationRatios.allowances || 1;
    const suggestedBasic = Math.round(baseForSplit * (allocationRatios.basic / ratioSum));
    const suggestedHra = Math.round(baseForSplit * (allocationRatios.hra / ratioSum));
    const suggestedAllowances = Math.max(0, gross - bonus - suggestedBasic - suggestedHra);
    const professionalTax = Math.round(gross * (profTaxRate / 100));
    const suggestions = {
      basic: suggestedBasic,
      hra: suggestedHra,
      allowances: suggestedAllowances,
      bonus,
      professionalTax,
    };
    setSalarySuggestions(suggestions);

    // Auto-fill fields only if user hasn't entered them (undefined or null)
    const currentBasic = form.getFieldValue(['salaryComponents', 'basic']);
    const currentHra = form.getFieldValue(['salaryComponents', 'hra']);
    const currentAllowances = form.getFieldValue(['salaryComponents', 'allowances']);
    const currentBonus = form.getFieldValue(['salaryComponents', 'bonus']);

    const currentProfessionalTax = form.getFieldValue(['salaryComponents', 'professionalTax']);
    const toSet = {};
    if ((currentBasic === undefined || currentBasic === null) && suggestions.basic) toSet.basic = suggestions.basic;
    if ((currentHra === undefined || currentHra === null) && suggestions.hra) toSet.hra = suggestions.hra;
    if ((currentAllowances === undefined || currentAllowances === null) && suggestions.allowances) toSet.allowances = suggestions.allowances;
    if ((currentBonus === undefined || currentBonus === null) && suggestions.bonus) toSet.bonus = suggestions.bonus;
    if ((currentProfessionalTax === undefined || currentProfessionalTax === null) && suggestions.professionalTax) toSet.professionalTax = suggestions.professionalTax;

    toSet.professionalTax = suggestions.professionalTax;
    if (Object.keys(toSet).length > 0) {
      const key = JSON.stringify(toSet);
      if (lastAutoFillRef.current !== key) {
        lastAutoFillRef.current = key;
        form.setFieldsValue({ salaryComponents: toSet });
      }
    }
  }, [salaryComponents, form, allocationRatios, profTaxRate, modalOpen]);

  useEffect(() => { load(); loadSummary(); loadUsers(); loadFunds(); loadBranches(); loadExpenseAccounts(); }, []);
  useEffect(() => { load(); }, [pagination.current, pagination.pageSize, categoryFilter, statusFilter, branchFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchExpenses({
        page: pagination.current, limit: pagination.pageSize,
        ...(categoryFilter ? { category: categoryFilter } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(branchFilter ? { branch: branchFilter } : {}),
      });
      setData(res.data || []);
      setPagination((p) => ({ ...p, total: res.meta?.total || 0 }));
    } catch { messageApi.error('Failed to load expenses'); }
    finally { setLoading(false); }
  };

  // Open the voucher PDF in a new tab for the browser's print dialog. The tab is
  // opened before the request so the popup blocker treats it as a user action.
  const openVoucherPdf = async (fetcher) => {
    const tab = window.open('', '_blank');
    setPrinting(true);
    try {
      const blob = await fetcher();
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      if (tab) tab.location.href = url;
      else window.location.assign(url);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (tab) tab.close();
      // Errors arrive as a blob too — read the server's message out of it.
      let msg = 'Failed to generate voucher';
      try {
        const body = JSON.parse(await err?.response?.data?.text());
        if (body?.message) msg = body.message;
      } catch { /* keep the generic message */ }
      messageApi.error(msg);
    } finally {
      setPrinting(false);
    }
  };

  const printVoucher = (r) => openVoucherPdf(() => fetchExpenseVoucherPdf(r._id));

  const printSelectedVouchers = () =>
    openVoucherPdf(() => fetchExpenseVouchersPdf({ ids: selectedRowKeys.join(',') }));

  const printFilteredVouchers = () =>
    openVoucherPdf(() => fetchExpenseVouchersPdf({
      ...(categoryFilter ? { category: categoryFilter } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(branchFilter ? { branch: branchFilter } : {}),
    }));

  const loadSummary = async () => {
    try { const res = await getExpenseSummary(); setSummary(res.data); }
    catch { /* non-critical */ }
  };

  const loadUsers = async () => {
    try {
      const res = await fetchUsers();
      setUsers((res.data || res || []).map((u) => ({
        value: u._id, label: `${u.name} (${u.designation || u.role || ''})`,
      })));
    } catch { /* non-critical */ }
  };
  
  const loadFunds = async () => {
    try {
      const res = await fetchFunds();
      setFunds(res.data || []);
    } catch {
      // Silently fail - funds are optional for payment
    }
  };

  const loadBranches = async () => {
    try {
      const res = await fetchBranches({ limit: 1000 });
      setBranches(res?.data?.data || res?.data || []);
    } catch {
      // Non-critical - branch tagging is optional
    }
  };

  const loadExpenseAccounts = async () => {
    try {
      const res = await getAccounts({ type: 'EXPENSE', isActive: 'true' });
      setExpenseAccounts(res?.data || []);
    } catch {
      // Non-critical - the expense falls back to 5300 Office & Administrative
    }
  };

  const openCreate = () => {
    setEditRecord(null);
    form.resetFields();
    form.setFieldsValue({
      status: 'APPROVED', recurrence: 'ONE_TIME', expenseDate: dayjs(),
      expenseAccountCode: DEFAULT_EXPENSE_ACCOUNT_CODE,
      paymentSplits: [{ mode: 'BANK_TRANSFER', amount: null, reference: '' }],
    });
    lastAutoFillRef.current = null;
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditRecord(record);
    form.setFieldsValue({
      ...record,
      expenseDate: record.expenseDate ? dayjs(record.expenseDate) : null,
      dueDate: record.dueDate ? dayjs(record.dueDate) : null,
      paidDate: record.paidDate ? dayjs(record.paidDate) : null,
      employeeId: record.employeeId?._id || record.employeeId,
      branch: record.branch?._id || record.branch || undefined,
      // Left blank on records created before the account was selectable — sending a
      // value the server never had would be rejected on a PAID expense.
      expenseAccountCode: record.expenseAccountCode || undefined,
      salaryMonth: record.salaryMonth ? dayjs(record.salaryMonth, 'YYYY-MM') : undefined,
      paymentSplits: record.paymentSplits?.length
        ? record.paymentSplits
        : [{ mode: record.paymentMode || 'BANK_TRANSFER', amount: record.totalAmount || record.amount || null, reference: record.transactionReference || '' }],
    });
    lastAutoFillRef.current = null;
    setModalOpen(true);
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    const { sourceFundId, paymentSplits, ...rest } = values;

    const payload = {
      ...rest,
      paymentSplits,
      expenseDate: values.expenseDate?.toISOString(),
      dueDate: values.dueDate?.toISOString() || null,
      paidDate: values.paidDate?.toISOString() || null,
      salaryMonth: values.salaryMonth ? values.salaryMonth.format('YYYY-MM') : undefined,
      ...(sourceFundId && !editRecord ? { sourceFundId } : {}),
    };
    try {
      if (editRecord) {
        await updateExpense(editRecord._id, payload);
        messageApi.success('Expense updated');
      } else {
        await createExpense(payload);
        messageApi.success('Expense recorded');
      }
      setModalOpen(false);
      form.resetFields();
      setEditRecord(null);
      load(); loadSummary();
    } catch (e) { messageApi.error(e.response?.data?.message || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (record) => {
    try {
      const res = await deleteExpense(record._id);
      messageApi.success(res?.data?.status === 'REVERSED' ? 'Expense reversed — funds returned to source' : 'Deleted');
      load(); loadSummary();
    } catch (e) { messageApi.error(e.response?.data?.message || 'Failed to delete'); }
  };
  
  const openPayment = (expense) => {
    setSelectedExpense(expense);
    setDenomCounts({});
    setDrawerBalance(null);
    paymentForm.resetFields();
    paymentForm.setFieldsValue({
      paymentMode: expense.paymentMode || 'BANK_TRANSFER',
      paymentDate: dayjs(),
    });
    // A branch-tagged expense defaults to paying from that branch's cash drawer.
    const branchId = expense.branch?._id || expense.branch;
    if (branchId) {
      setPayMode('DRAWER');
      fetchCashLedgerBalance({ branchId })
        .then((r) => setDrawerBalance(r?.data?.data?.runningBalance ?? null))
        .catch(() => setDrawerBalance(null));
    } else {
      setPayMode('FUND');
    }
    setPaymentModalOpen(true);
  };

  const denomTotal = Object.entries(denomCounts).reduce((s, [k, n]) => {
    const val = Number(String(k).replace('note', '')) || 0;
    return s + val * (Number(n) || 0);
  }, 0);

  const handlePayExpense = async (values) => {
    if (!selectedExpense) return;

    const amount = selectedExpense.totalAmount || 0;
    const hasDenoms = denomTotal > 0;

    if (payMode === 'DRAWER' && hasDenoms && Math.round(denomTotal) !== Math.round(amount)) {
      messageApi.error(`Denomination total (${fmt(denomTotal)}) must equal the expense amount (${fmt(amount)})`);
      return;
    }

    setPaymentLoading(true);
    try {
      const payload = payMode === 'DRAWER'
        ? {
            source: 'DRAWER',
            businessDate: values.paymentDate?.toISOString(),
            ...(hasDenoms ? { denominations: denomCounts } : {}),
            remarks: values.notes,
          }
        : {
            source: 'FUND',
            fundId: values.fundId,
            paymentMode: values.paymentMode,
            paymentReference: values.paymentReference,
            paymentDate: values.paymentDate?.toISOString(),
            remarks: values.notes,
          };
      await payExpense(selectedExpense._id, payload);
      messageApi.success(payMode === 'DRAWER' ? 'Expense paid from branch drawer' : 'Expense paid from fund');
      setPaymentModalOpen(false);
      paymentForm.resetFields();
      setDenomCounts({});
      load();
      loadSummary();
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to pay expense');
    } finally {
      setPaymentLoading(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'expenseId', key: 'id', width: 100 },
    { title: 'Voucher No', dataIndex: 'voucherNo', key: 'voucherNo', width: 140,
      render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Category', dataIndex: 'category', key: 'cat', width: 140,
      render: (v) => <Tag color={CATEGORY_COLORS[v] || 'default'}>{CATEGORY_MAP[v] || v}</Tag> },
    { title: 'Description', dataIndex: 'description', key: 'desc', width: 200, ellipsis: true },
    { title: 'Amount', dataIndex: 'totalAmount', key: 'amt', width: 120,
      render: (v) => <Text strong>{fmt(v)}</Text> },
    { title: 'Payee', dataIndex: 'payeeName', key: 'payee', width: 130,
      render: (v, r) => v || r.employeeId?.name || '—' },
    { title: 'Branch', dataIndex: 'branch', key: 'branch', width: 130,
      render: (v) => v?.name ? <Tag color="geekblue">{v.name}</Tag> : <Text type="secondary">—</Text> },
    { title: 'Mode', dataIndex: 'paymentMode', key: 'mode', width: 120,
      render: (v, record) => {
        if (v === 'SPLIT' && record.paymentSplits?.length) {
          return record.paymentSplits.map((s, i) => (
            <Tag key={i} color="blue" style={{ marginBottom: 2 }}>
              {s.mode?.replace(/_/g, ' ')} — {fmt(s.amount)}
            </Tag>
          ));
        }
        return v?.replace(/_/g, ' ') || '—';
      } },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 100,
      render: (v) => <Tag color={STATUS_COLORS[v]}>{v}</Tag> },
    { title: 'Date', dataIndex: 'expenseDate', key: 'date', width: 100,
      render: (v) => v ? new Date(v).toLocaleDateString('en-IN') : '—' },
    { title: 'Recurrence', dataIndex: 'recurrence', key: 'rec', width: 100,
      render: (v) => v?.replace(/_/g, ' ') || '—' },
    { title: 'Actions', key: 'actions', fixed: 'right', width: 190,
      render: (_, r) => (
        <Space size="small">
          <Button size="small" icon={<PrinterOutlined />} onClick={() => printVoucher(r)}
            disabled={printing} title="Print payment voucher" />
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} disabled={r.status === 'REVERSED'} />
          {r.status === 'APPROVED' && (
            <Button
              size="small"
              icon={<CreditCardOutlined />}
              onClick={() => openPayment(r)}
              type="primary"
              ghost
              title="Pay from Fund"
            >
              Pay
            </Button>
          )}
          {r.status !== 'REVERSED' && (
            <Popconfirm
              title={r.status === 'PAID' ? 'Reverse this expense?' : 'Delete this expense?'}
              description={r.status === 'PAID'
                ? 'The paid amount will be returned to the source fund and the ledger entry reversed.'
                : undefined}
              okText={r.status === 'PAID' ? 'Reverse' : 'Delete'}
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(r)}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}

      {/* Summary */}
      {summary && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={6}>
            <Card size="small"><Statistic title="Total Expenses" value={fmt(summary.grandTotal)} valueStyle={{ fontSize: 18, fontWeight: 600, color: '#cf1322' }} /></Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small"><Statistic title="Salary" value={fmt(summary.salary?.total)} valueStyle={{ fontSize: 18 }} /></Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic title="Marketing" value={fmt(summary.marketing?.total)} valueStyle={{ fontSize: 18 }} />
              <Text type="secondary" style={{ fontSize: 11 }}>
                Online: {fmt(summary.marketing?.online)} · Offline: {fmt(summary.marketing?.offline)}
              </Text>
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small"><Statistic title="Categories" value={summary.categoryBreakdown?.length || 0} suffix="types" valueStyle={{ fontSize: 18 }} /></Card>
          </Col>
        </Row>
      )}

      {/* Toolbar */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space>
            <Select placeholder="All Categories" allowClear style={{ width: 200 }}
              value={categoryFilter || undefined}
              onChange={(v) => { setPagination((p) => ({ ...p, current: 1 })); setCategoryFilter(v || ''); }}
              options={CATEGORIES}
            />
            <Select placeholder="All Statuses" allowClear style={{ width: 140 }}
              value={statusFilter || undefined}
              onChange={(v) => { setPagination((p) => ({ ...p, current: 1 })); setStatusFilter(v || ''); }}>
              {['PENDING', 'APPROVED', 'PAID', 'REJECTED', 'REVERSED'].map((s) => (
                <Select.Option key={s} value={s}>{s}</Select.Option>
              ))}
            </Select>
            <Select placeholder="All Branches" allowClear showSearch style={{ width: 200 }}
              value={branchFilter || undefined} optionFilterProp="label"
              onChange={(v) => { setPagination((p) => ({ ...p, current: 1 })); setBranchFilter(v || ''); }}
              options={branches.map((b) => ({ value: b._id, label: `${b.name}${b.code ? ` (${b.code})` : ''}` }))}
            />
            <Button icon={<ReloadOutlined />} onClick={() => { load(); loadSummary(); }}>Refresh</Button>
          </Space>
        </Col>
        <Col>
          <Space>
            <Dropdown
              disabled={printing}
              menu={{
                items: [
                  { key: 'selected', label: `Print selected (${selectedRowKeys.length})`, disabled: !selectedRowKeys.length },
                  { key: 'filtered', label: `Print all matching filters (${pagination.total})`, disabled: !pagination.total },
                  ...(selectedRowKeys.length ? [{ type: 'divider' }, { key: 'clear', label: 'Clear selection' }] : []),
                ],
                onClick: ({ key }) => {
                  if (key === 'selected') printSelectedVouchers();
                  else if (key === 'filtered') printFilteredVouchers();
                  else if (key === 'clear') setSelectedRowKeys([]);
                },
              }}
            >
              <Button icon={<PrinterOutlined />} loading={printing}>
                Print Vouchers <DownOutlined />
              </Button>
            </Dropdown>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add Expense
            </Button>
          </Space>
        </Col>
      </Row>

      <DataTable persistId="accounting.expenses.main" serverPaginated
        dataSource={data} rowKey="_id" columns={columns}
        loading={loading} size="small" scroll={{ x: 1400 }}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          preserveSelectedRowKeys: true,
        }}
        pagination={{
          current: pagination.current, pageSize: pagination.pageSize, total: pagination.total,
          showSizeChanger: true,
          onChange: (p, s) => setPagination({ current: p, pageSize: s, total: pagination.total }),
        }}
      />

      {/* ── Create / Edit Modal ── */}
      <Modal
        title={editRecord ? `Edit Expense: ${editRecord.expenseId}` : 'Add Expense'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditRecord(null); }}
        onOk={() => form.submit()}
        confirmLoading={submitting}
        okText={editRecord ? 'Update' : 'Save'}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Category" name="category" rules={[{ required: true }]}>
                <Select
                  options={CATEGORIES}
                  placeholder="Select category"
                  onChange={(v) => {
                    // Point the debit at the account this category normally uses;
                    // still editable below. Never touched on a paid expense, whose
                    // account is locked to what already posted.
                    if (editRecord?.status === 'PAID') return;
                    const suggested = EXPENSE_CATEGORY_MAP[v]?.account;
                    if (suggested && expenseAccounts.some((a) => a.code === suggested)) {
                      form.setFieldsValue({ expenseAccountCode: suggested });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Sub-category" name="subCategory">
                <Input placeholder="e.g. Google Ads, Petrol, etc." />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Description" name="description" rules={[{ required: true }]}>
            <Input placeholder="Brief description of the expense" />
          </Form.Item>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item label="Amount (₹)" name="amount" rules={[{ required: true }]}>
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Tax Amount" name="taxAmount" initialValue={0}>
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider plain style={{ margin: '8px 0 12px' }}>GST Details (optional — enables Input Tax Credit)</Divider>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item label="Supplier GSTIN" name={['gst', 'gstin']}>
                <Input placeholder="e.g. 19ABCDE1234F1Z5" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Supplier Name" name={['gst', 'partyName']}>
                <Input placeholder="Vendor / supplier" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="GST Type" name={['gst', 'gstType']}>
                <Select allowClear placeholder="Select"
                  options={[
                    { value: 'INTRA', label: 'Intra-state (CGST + SGST)' },
                    { value: 'INTER', label: 'Inter-state (IGST)' },
                  ]} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item label="CGST (₹)" name={['gst', 'cgst']} initialValue={0}>
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="SGST (₹)" name={['gst', 'sgst']} initialValue={0}>
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="IGST (₹)" name={['gst', 'igst']} initialValue={0}>
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item noStyle shouldUpdate={(p, c) => p.gst !== c.gst || p.taxAmount !== c.taxAmount}>
                {({ getFieldValue }) => {
                  const g = getFieldValue('gst') || {};
                  const gstTotal = (Number(g.cgst) || 0) + (Number(g.sgst) || 0) + (Number(g.igst) || 0);
                  const tax = Number(getFieldValue('taxAmount')) || 0;
                  if (!gstTotal) return null;
                  const mismatch = Math.abs(gstTotal - tax) > 0.01;
                  return (
                    <div style={{ paddingTop: 30, fontSize: 12 }}>
                      <span style={{ color: mismatch ? '#fa8c16' : '#52c41a' }}>
                        GST total ₹{gstTotal.toFixed(2)}{mismatch ? ` — set Tax Amount to match` : ' ✓'}
                      </span>
                    </div>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>

          <Divider plain style={{ margin: '8px 0 12px' }}>Payment Breakdown</Divider>

          <Form.Item noStyle shouldUpdate={(prev, cur) =>
            prev.amount !== cur.amount || prev.taxAmount !== cur.taxAmount
          }>
            {({ getFieldValue }) => {
              const amt = getFieldValue('amount') || 0;
              const tax = getFieldValue('taxAmount') || 0;
              return <PaymentSplitInput fieldName="paymentSplits" totalAmount={amt + tax} />;
            }}
          </Form.Item>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item label="Expense Date" name="expenseDate" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Due Date" name="dueDate">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Paid Date" name="paidDate">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item label="Payee Name" name="payeeName">
                <Input placeholder="Vendor / Landlord" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Txn Reference" name="transactionReference">
                <Input placeholder="UTR / Invoice #" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Recurrence" name="recurrence" initialValue="ONE_TIME">
                <Select options={RECURRENCES} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item label="Status" name="status" initialValue="APPROVED">
                <Select options={[
                  { value: 'PENDING', label: 'Pending' },
                  { value: 'APPROVED', label: 'Approved' },
                  { value: 'PAID', label: 'Paid' },
                  { value: 'REJECTED', label: 'Rejected' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Branch"
                name="branch"
                tooltip="Which branch this expense belongs to — drives per-branch spend reporting, regardless of how it's paid."
              >
                <Select
                  placeholder="None (company-wide)"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={branches.map((b) => ({ value: b._id, label: `${b.name}${b.code ? ` (${b.code})` : ''}` }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Fiscal Year" name="fiscalYear">
                <Input placeholder="Auto-calculated if blank" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Expense Account (Chart of Accounts)"
            name="expenseAccountCode"
            tooltip="The account debited when this expense posts to the ledger. Pre-filled from the category; blank posts to 5300 Office & Administrative. Locked once the expense is paid."
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              disabled={editRecord?.status === 'PAID'}
              placeholder="5300 — Office & Administrative (default)"
              options={expenseAccounts.map((a) => ({ value: a.code, label: `${a.code} — ${a.name}` }))}
            />
          </Form.Item>

          {/* Auto-debit from fund (create only) */}
          {!editRecord && (
            <Row gutter={12}>
              <Col span={24}>
                <Form.Item 
                  label="Auto-Pay from Fund (optional)" 
                  name="sourceFundId"
                  tooltip="If selected, the expense will be immediately PAID by debiting this fund"
                >
                  <Select placeholder="None — create as pending/approved" allowClear>
                    {funds.map(fund => (
                      <Select.Option key={fund._id} value={fund._id}>
                        {fund.name} ({fmt(fund.balance)})
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* Salary-specific fields */}
          {category === 'SALARY' && (
            <>
              <Divider orientation="left" plain>Salary Details</Divider>
              <Row gutter={[12, 12]} style={{ marginBottom: 8 }}>
                <Col span={24}>
                  <Space wrap size={12} align="baseline">
                    <Typography.Text type="secondary">Ratios (%)</Typography.Text>
                    <InputNumber size="small" min={0} max={100} value={allocationRatios.basic}
                      onChange={(value) => setAllocationRatios((r) => ({ ...r, basic: Number(value || 0) }))}
                      style={{ width: 110 }} placeholder="Basic" />
                    <InputNumber size="small" min={0} max={100} value={allocationRatios.hra}
                      onChange={(value) => setAllocationRatios((r) => ({ ...r, hra: Number(value || 0) }))}
                      style={{ width: 110 }} placeholder="HRA" />
                    <InputNumber size="small" min={0} max={100} value={allocationRatios.allowances}
                      onChange={(value) => setAllocationRatios((r) => ({ ...r, allowances: Number(value || 0) }))}
                      style={{ width: 110 }} placeholder="Allowances" />
                    <Typography.Text type="secondary">Prof. Tax rate</Typography.Text>
                    <InputNumber size="small" min={0} max={100} value={profTaxRate}
                      onChange={(value) => setProfTaxRate(Number(value || 0))}
                      style={{ width: 110 }} formatter={(val) => `${val}%`} parser={(val) => val?.replace('%', '')} />
                  </Space>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Employee" name="employeeId">
                    <Select showSearch placeholder="Select employee" options={users} allowClear
                      filterOption={(input, opt) => opt.label?.toLowerCase().includes(input.toLowerCase())} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Salary Month" name="salaryMonth">
                    <DatePicker picker="month" format="YYYY-MM" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item label="Basic" name={['salaryComponents', 'basic']} extra={salarySuggestions.basic ? `Suggested: ${fmt(salarySuggestions.basic)}` : null}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="HRA" name={['salaryComponents', 'hra']} extra={salarySuggestions.hra ? `Suggested: ${fmt(salarySuggestions.hra)}` : null}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Allowances" name={['salaryComponents', 'allowances']} extra={salarySuggestions.allowances ? `Suggested: ${fmt(salarySuggestions.allowances)}` : null}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={6}>
                  <Form.Item label="Deductions" name={['salaryComponents', 'deductions']}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Bonus" name={['salaryComponents', 'bonus']} extra={salarySuggestions.bonus ? `Suggested: ${fmt(salarySuggestions.bonus)}` : null}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Professional Tax" name={['salaryComponents', 'professionalTax']} extra={salarySuggestions.professionalTax ? `Suggested: ${fmt(salarySuggestions.professionalTax)}` : null}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Net Pay" name={['salaryComponents', 'netPay']}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          <Form.Item label="Remarks" name="remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
      
      {/* Payment Modal */}
      <Modal
        title={`Pay Expense - ${selectedExpense?.description || ''}`}
        open={paymentModalOpen}
        onCancel={() => setPaymentModalOpen(false)}
        onOk={() => paymentForm.submit()}
        confirmLoading={paymentLoading}
        width={620}
      >
        <Form
          form={paymentForm}
          layout="vertical"
          onFinish={handlePayExpense}
        >
          {selectedExpense && (
            <div style={{ marginBottom: 16, padding: 16, background: '#fafafa', borderRadius: 6 }}>
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <Text strong>Amount: </Text>
                  <Text>{fmt(selectedExpense.totalAmount)}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Category: </Text>
                  <Text>{CATEGORY_MAP[selectedExpense.category] || selectedExpense.category}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Payee: </Text>
                  <Text>{selectedExpense.payeeName || selectedExpense.employeeId?.name || 'N/A'}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Branch: </Text>
                  <Text>{selectedExpense.branch?.name || '— (company-wide)'}</Text>
                </Col>
              </Row>
            </div>
          )}

          {/* Payment source — a branch-tagged expense can be paid from the branch drawer */}
          {selectedExpense?.branch && (
            <Form.Item label="Pay From">
              <Radio.Group value={payMode} onChange={(e) => setPayMode(e.target.value)}>
                <Radio.Button value="DRAWER">Branch Cash Drawer</Radio.Button>
                <Radio.Button value="FUND">Central Fund (Bank/HO)</Radio.Button>
              </Radio.Group>
            </Form.Item>
          )}

          {payMode === 'DRAWER' ? (
            <>
              <Alert
                type={drawerBalance !== null && drawerBalance < (selectedExpense?.totalAmount || 0) ? 'error' : 'info'}
                showIcon
                style={{ marginBottom: 12 }}
                message={
                  drawerBalance === null
                    ? 'Loading branch drawer balance…'
                    : `Branch drawer balance: ${fmt(drawerBalance)}`
                }
                description={
                  drawerBalance !== null && drawerBalance < (selectedExpense?.totalAmount || 0)
                    ? 'Insufficient cash in the drawer. Fund the branch before paying this expense in cash.'
                    : 'Cash will be paid out of this branch’s physical drawer on the selected day.'
                }
              />

              <Form.Item name="paymentDate" label="Payment Date (business day)" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>

              <Divider plain style={{ margin: '4px 0 12px' }}>
                Denomination breakdown (optional — must equal {fmt(selectedExpense?.totalAmount || 0)})
              </Divider>
              <DenominationCountForm
                counts={denomCounts}
                onChange={setDenomCounts}
                expectedCash={selectedExpense?.totalAmount || 0}
              />

              <Form.Item name="notes" label="Payment Notes" style={{ marginTop: 12 }}>
                <Input.TextArea rows={2} placeholder="Additional notes about this payment" />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item name="fundId" label="Select Fund" rules={[{ required: true }]}>
                <Select placeholder="Choose fund to debit">
                  {funds.map(fund => (
                    <Select.Option key={fund._id} value={fund._id}>
                      {fund.name} ({fmt(fund.balance)})
                      {fund.balance < (selectedExpense?.totalAmount || 0) && (
                        <Text type="danger" style={{ marginLeft: 8 }}>(Insufficient)</Text>
                      )}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="paymentMode" label="Payment Mode" rules={[{ required: true }]}>
                <Select>
                  {PAYMENT_MODES.map(mode => (
                    <Select.Option key={mode} value={mode}>
                      {mode.replace(/_/g, ' ')}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="paymentDate" label="Payment Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item name="paymentReference" label="Payment Reference">
                <Input placeholder="Transaction ID, check number, etc." />
              </Form.Item>

              <Form.Item name="notes" label="Payment Notes">
                <Input.TextArea rows={2} placeholder="Additional notes about this payment" />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
}
